import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, DollarSign, LogOut, Plus, CreditCard, Clock, CheckCircle, XCircle, Handshake } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

export default function PartnerDashboard() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ company_name: "", title: "", description: "", video_url: "", thumbnail_url: "", tier: "standard", duration_weeks: 1 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meRes, videosRes, tiersRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/partner/my-videos`, { withCredentials: true }),
          axios.get(`${API}/sponsored-tiers`),
        ]);
        setUser(meRes.data);
        setVideos(videosRes.data);
        setTiers(tiersRes.data);
        setForm((prev) => ({ ...prev, company_name: meRes.data.name || "" }));
      } catch {
        toast.error("Please sign in.");
        navigate("/partner");
      } finally { setLoading(false); }
    };
    fetchData();
  }, [navigate]);

  // Handle payment success redirect
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success" && sessionId) {
      const activateVideo = async () => {
        const pendingVideo = videos.find((v) => v.status === "pending_payment");
        if (pendingVideo) {
          try {
            await axios.post(`${API}/partner/videos/${pendingVideo.id}/activate`, { session_id: sessionId }, { withCredentials: true });
            setVideos((prev) => prev.map((v) => v.id === pendingVideo.id ? { ...v, status: "active", paid: true } : v));
            toast.success("Payment confirmed! Your video is now live.");
          } catch { /* will retry on reload */ }
        }
      };
      activateVideo();
    }
  }, [searchParams, videos]);

  const handleSubmit = async () => {
    if (!form.title || !form.video_url) { toast.error("Title and video URL are required."); return; }
    try {
      const res = await axios.post(`${API}/partner/videos`, form, { withCredentials: true });
      setVideos((prev) => [res.data, ...prev]);
      setShowSubmit(false);
      setForm({ company_name: user?.name || "", title: "", description: "", video_url: "", thumbnail_url: "", tier: "standard", duration_weeks: 1 });
      toast.success("Video submitted. Proceed to payment to go live.");
    } catch (err) {
      toast.error("Failed to submit video.");
    }
  };

  const handlePay = async (videoId) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/partner/videos/pay`, { video_id: videoId, origin_url: origin }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      toast.error("Payment failed. Please try again.");
    }
  };

  const statusIcon = (status) => {
    if (status === "active") return <CheckCircle size={14} className="text-green-400" />;
    if (status === "pending_payment") return <Clock size={14} className="text-gold" />;
    return <XCircle size={14} className="text-red-400" />;
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold font-body animate-pulse">Loading...</div></div>;

  const selectedTier = tiers.find((t) => t.id === form.tier);

  return (
    <div data-testid="partner-dashboard" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gold/15 flex items-center justify-center"><Handshake size={22} className="text-gold" /></div>
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-0.5">Partner Dashboard</p>
              <h1 className="font-heading text-2xl text-white">{user?.name || "Partner"}</h1>
            </div>
          </div>
          <button data-testid="partner-logout" onClick={async () => { try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {} navigate("/partner"); }} className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-body transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>

        {/* Pricing Tiers */}
        <motion.div initial="hidden" animate="visible" className="mb-10">
          <motion.h3 variants={fadeUp} custom={0} className="text-white font-body text-sm font-medium mb-4">Promotion Tiers</motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((tier, i) => (
              <motion.div key={tier.id} variants={fadeUp} custom={i} className="glass-card p-6">
                <h4 className="text-gold font-body text-sm font-medium mb-1">{tier.name}</h4>
                <p className="text-white font-heading text-2xl mb-2">${tier.amount}<span className="text-white/30 text-sm font-body">/week</span></p>
                <p className="text-white/40 font-body text-xs">{tier.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Submit Video */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-body text-sm font-medium">My Sponsored Videos ({videos.length})</h3>
          <button data-testid="submit-video-btn" onClick={() => setShowSubmit(!showSubmit)} className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors">
            <Plus size={14} /> Submit Video
          </button>
        </div>

        {showSubmit && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 mb-6 space-y-4">
            <h4 className="text-white font-body text-sm font-medium">Submit a Sponsored Video</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Company Name *</label>
                <Input data-testid="video-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Video Title *</label>
                <Input data-testid="video-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event or promo title" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Video URL * (YouTube, Vimeo, or direct)</label>
                <Input data-testid="video-url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Thumbnail URL (optional)</label>
                <Input data-testid="video-thumbnail" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Tier</label>
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="video-tier" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-luxury-charcoal border-white/10">
                    {tiers.map((t) => <SelectItem key={t.id} value={t.id} className="text-white/80 focus:bg-gold/10 text-sm">{t.name} - ${t.amount}/wk</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white/40 text-xs font-body mb-1 block">Duration (weeks)</label>
                <Select value={String(form.duration_weeks)} onValueChange={(v) => setForm({ ...form, duration_weeks: parseInt(v) })}>
                  <SelectTrigger data-testid="video-duration" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-luxury-charcoal border-white/10">
                    {[1, 2, 4, 8, 12].map((w) => <SelectItem key={w} value={String(w)} className="text-white/80 text-sm">{w} week{w > 1 ? "s" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-white/40 text-xs font-body mb-1 block">Description</label>
              <Textarea data-testid="video-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." rows={2} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm text-sm resize-none" />
            </div>
            {selectedTier && (
              <div className="flex items-center justify-between p-4 bg-gold/5 border border-gold/20">
                <span className="text-white/60 text-sm font-body">Total: {selectedTier.name} x {form.duration_weeks} week{form.duration_weeks > 1 ? "s" : ""}</span>
                <span className="text-gold font-body font-medium text-lg">${(selectedTier.amount * form.duration_weeks).toLocaleString()}</span>
              </div>
            )}
            <button data-testid="submit-video-confirm" onClick={handleSubmit} className="bg-gold text-black px-8 py-3 text-sm font-body font-medium hover:bg-gold-light transition-colors">Submit & Proceed to Payment</button>
          </motion.div>
        )}

        {/* Video List */}
        <div className="space-y-4">
          {videos.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Video size={48} className="text-gold/30 mx-auto mb-4" />
              <h3 className="text-white font-heading text-xl mb-2">No Videos Yet</h3>
              <p className="text-white/40 font-body text-sm">Submit your first sponsored video to get started.</p>
            </div>
          ) : videos.map((v, i) => (
            <motion.div key={v.id} initial="hidden" animate="visible" variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`partner-video-${v.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {statusIcon(v.status)}
                    <h4 className="text-white font-body text-sm font-medium">{v.title}</h4>
                    <span className={`px-2 py-0.5 text-[10px] font-body uppercase ${
                      v.status === "active" ? "bg-green-500/15 text-green-400" :
                      v.status === "pending_payment" ? "bg-gold/15 text-gold" : "bg-white/10 text-white/40"
                    }`}>{v.status.replace("_", " ")}</span>
                  </div>
                  <div className="flex gap-4 text-white/30 text-xs font-body mt-1">
                    <span>{v.tier} tier</span>
                    <span>{v.duration_weeks}wk</span>
                    <span>${v.total_cost?.toLocaleString()}</span>
                  </div>
                </div>
                {v.status === "pending_payment" && (
                  <button data-testid={`pay-video-${v.id}`} onClick={() => handlePay(v.id)} className="flex items-center gap-2 bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors">
                    <CreditCard size={14} /> Pay Now
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
